import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, Images, ArrowLeft } from "lucide-react";


interface Album {
  id: string;
  name: string;
  description: string | null;
  cover_url: string | null;
  photo_count: number;
}

interface Photo {
  id: string;
  photo_url: string;
  display_order: number | null;
}

const Galeria = () => {
  const { t } = useTranslation();
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);

  // Fetch published albums with photo count
  const { data: albums, isLoading: loadingAlbums } = useQuery({
    queryKey: ["gallery-albums"],
    queryFn: async () => {
      const { data: albumsData, error } = await supabase
        .from("photo_albums")
        .select("id, name, description, cover_url, is_published")
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get photo counts for each album
      const albumsWithCounts = await Promise.all(
        (albumsData || []).map(async (album) => {
          const { count } = await supabase
            .from("album_photos")
            .select("*", { count: "exact", head: true })
            .eq("album_id", album.id);
          return { ...album, photo_count: count || 0 };
        })
      );

      return albumsWithCounts as Album[];
    },
  });

  // Fetch photos for selected album
  const { data: photos, isLoading: loadingPhotos } = useQuery({
    queryKey: ["album-photos", selectedAlbum?.id],
    queryFn: async () => {
      if (!selectedAlbum) return [];
      const { data, error } = await supabase
        .from("album_photos")
        .select("id, photo_url, display_order")
        .eq("album_id", selectedAlbum.id)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as Photo[];
    },
    enabled: !!selectedAlbum,
  });

  const handlePrevPhoto = () => {
    if (selectedPhotoIndex !== null && photos) {
      setSelectedPhotoIndex(
        selectedPhotoIndex === 0 ? photos.length - 1 : selectedPhotoIndex - 1
      );
    }
  };

  const handleNextPhoto = () => {
    if (selectedPhotoIndex !== null && photos) {
      setSelectedPhotoIndex(
        selectedPhotoIndex === photos.length - 1 ? 0 : selectedPhotoIndex + 1
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") handlePrevPhoto();
    if (e.key === "ArrowRight") handleNextPhoto();
    if (e.key === "Escape") setSelectedPhotoIndex(null);
  };

  return (
    <Layout>
      <SEO
        title={t("gallery.seoTitle")}
        description={t("gallery.seoDescription")}
      />

      {/* Header */}
      <section className="relative py-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-primary mb-4 animate-fade-in">
              {t("gallery.title")}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground font-body">
              {t("gallery.subtitle")}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          {/* Album View or Album Grid */}
          {selectedAlbum ? (
            <div className="animate-fade-in">
              {/* Back button and album title */}
              <div className="flex items-center gap-4 mb-8">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedAlbum(null)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  {t("gallery.backToAlbums")}
                </Button>
              </div>

              <div className="text-center mb-8">
                <h2 className="font-display text-3xl text-primary mb-2">
                  {selectedAlbum.name}
                </h2>
                {selectedAlbum.description && (
                  <p className="text-muted-foreground">{selectedAlbum.description}</p>
                )}
              </div>

              {/* Photos Grid */}
              {loadingPhotos ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-muted animate-pulse rounded-lg"
                    />
                  ))}
                </div>
              ) : photos && photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo, index) => (
                    <div
                      key={photo.id}
                      className="aspect-square rounded-lg overflow-hidden cursor-pointer group relative"
                      onClick={() => setSelectedPhotoIndex(index)}
                    >
                      <img
                        src={photo.photo_url}
                        alt={`${selectedAlbum.name} - ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Images className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{t("gallery.noPhotos")}</p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Albums Grid */}
              {loadingAlbums ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i} className="overflow-hidden">
                      <div className="aspect-[4/3] bg-muted animate-pulse" />
                      <CardContent className="p-4">
                        <div className="h-6 bg-muted animate-pulse rounded mb-2" />
                        <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : albums && albums.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {albums.map((album) => (
                    <Card
                      key={album.id}
                      className="overflow-hidden cursor-pointer group hover:shadow-lg transition-all duration-300"
                      onClick={() => setSelectedAlbum(album)}
                    >
                      <div className="aspect-[4/3] relative overflow-hidden bg-muted">
                        {album.cover_url ? (
                          <img
                            src={album.cover_url}
                            alt={album.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-primary/10">
                            <Images className="w-16 h-16 text-primary/50" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-display text-xl text-primary mb-1 group-hover:text-primary/80 transition-colors">
                          {album.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {album.photo_count} {t("gallery.photos")}
                        </p>
                        {album.description && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {album.description}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Images className="w-20 h-20 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-display text-2xl text-primary mb-2">
                    {t("gallery.noAlbums")}
                  </h3>
                  <p className="text-muted-foreground">{t("gallery.noAlbumsDesc")}</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog
        open={selectedPhotoIndex !== null}
        onOpenChange={(open) => !open && setSelectedPhotoIndex(null)}
      >
        <DialogContent
          className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none"
          onKeyDown={handleKeyDown}
        >
          <DialogTitle className="sr-only">{t("gallery.viewPhoto")}</DialogTitle>
          
          {selectedPhotoIndex !== null && photos && photos[selectedPhotoIndex] && (
            <div className="relative w-full h-[90vh] flex items-center justify-center">
              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
                onClick={() => setSelectedPhotoIndex(null)}
              >
                <X className="w-6 h-6" />
              </Button>

              {/* Navigation buttons */}
              {photos.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-4 z-50 text-white hover:bg-white/20 h-12 w-12"
                    onClick={handlePrevPhoto}
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-4 z-50 text-white hover:bg-white/20 h-12 w-12"
                    onClick={handleNextPhoto}
                  >
                    <ChevronRight className="w-8 h-8" />
                  </Button>
                </>
              )}

              {/* Image */}
              <img
                src={photos[selectedPhotoIndex].photo_url}
                alt={`${selectedAlbum?.name} - ${selectedPhotoIndex + 1}`}
                className="max-w-full max-h-full object-contain"
              />

              {/* Photo counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
                {selectedPhotoIndex + 1} / {photos.length}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Galeria;